function configs(labels, datasetsInput, chartConfig = {}) {
  const datasetsArray = Array.isArray(datasetsInput) ? datasetsInput : [datasetsInput];
  const { onPointClick, xAxisTickRotation } = chartConfig;
  const formatTooltipDate = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const raw = String(value).split("T")[0];
    const isoParts = raw.split("-");
    const isoMonthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    if (isoParts.length === 3 && isoParts[0].length === 4) {
      return `${isoParts[2]} ${isoMonthNames[Number(isoParts[1]) - 1]}`;
    }
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const iso = raw.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
    if (iso) return `${iso[3]} ${months[Number(iso[2]) - 1]}`;
    const dmy = raw.match(/^(\\d{2})-(\\d{2})-(\\d{4})$/);
    if (dmy) return `${dmy[1]} ${months[Number(dmy[2]) - 1]}`;
    return raw;
  };
  const formatDayDateTitle = (label, metadata = {}) => {
    const day = metadata.day ?? (/^-?\\d+(?:\\.\\d+)?$/.test(String(label)) ? label : null);
    if (day === null || day === undefined) return label ?? "";
    const date = formatTooltipDate(metadata.date);
    return `D${day}${date ? ` (${date})` : ""}`;
  };

  const datasets = datasetsArray.map((dataset) => ({
    label: dataset.label,
    data: dataset.data,
    type: dataset.type || "line",
    yAxisID: dataset.yAxisID || "y",
    backgroundColor: dataset.backgroundColor,
    borderColor: dataset.borderColor || "transparent",
    stack: dataset.stack,
    pointBackgroundColor: dataset.pointBackgroundColor,
    pointBorderColor: dataset.pointBorderColor,
    pointBorderWidth: dataset.pointBorderWidth,
    pointHoverBackgroundColor: dataset.pointHoverBackgroundColor,
    pointHoverBorderColor: dataset.pointHoverBorderColor,
    pointHoverBorderWidth: dataset.pointHoverBorderWidth,
    barThickness: dataset.barThickness ?? 12,
    maxBarThickness: dataset.maxBarThickness ?? 12,
    barPercentage: dataset.barPercentage ?? 0.72,
    categoryPercentage: dataset.categoryPercentage ?? 0.9,
    borderRadius: dataset.borderRadius ?? 4,
    borderSkipped: false,
    spanGaps: dataset.spanGaps ?? false,
    tension: dataset.tension ?? 0.4,
    fill: dataset.fill ?? false,
    pointRadius: dataset.pointRadius ?? 3,
    pointHoverRadius: dataset.pointHoverRadius ?? 5,
    borderWidth: dataset.borderWidth ?? 1,
    borderDash: dataset.borderDash,
    metadata: dataset.metadata,
    datalabels: dataset.datalabels,
    order: dataset.order,
    showLegend: dataset.showLegend,
  }));

  const showRightAxis = datasetsArray.some((dataset) => (dataset.yAxisID || "").includes("y1"));
  const showStacked = datasetsArray.some((dataset) => dataset.stacked === true);

  const y1Values = datasetsArray
    .filter((dataset) => (dataset.yAxisID || "").includes("y1"))
    .flatMap((dataset) =>
      Array.isArray(dataset.data)
        ? dataset.data.map((value) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : null;
          })
        : []
    )
    .filter((value) => value !== null);
  const y1MaxRaw = y1Values.length ? Math.max(...y1Values) : null;
  const y1Max = y1MaxRaw !== null ? Math.ceil(y1MaxRaw) : null;
  const y1Min = y1Max !== null ? Math.max(0, y1Max - 4) : null;
  const y1SuggestedMax = y1Max !== null ? y1Max : undefined;
  const y1SuggestedMin = y1Min !== null ? y1Min : undefined;
  const y1Step = 0.5;

  //const rawMax = datasetsArray[0]?.yAxisMax;
  //const maxY = rawMax ? Math.ceil(rawMax / 10) * 10 : undefined;

  return {
    data: {
      labels,
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: datasets.some((ds) => ds.showLegend !== false),
          position: "top",
          labels: {
            usePointStyle: true,
            generateLabels: function (chart) {
              const datasets = chart.data.datasets;
              return datasets
                .map((dataset, i) => ({ dataset, datasetIndex: i }))
                .filter(({ dataset }) => dataset.showLegend !== false)
                .map(({ dataset, datasetIndex }) => ({
                  text: dataset.label,
                  fillStyle: dataset.borderColor || dataset.backgroundColor || "#fff", // 👈 color source
                  strokeStyle: dataset.borderColor || "#000",
                  lineWidth: 4,
                  pointStyle: "line",
                  hidden: !chart.isDatasetVisible(datasetIndex),
                  datasetIndex,
                  fontColor: "#D8D8D8",
                }));
            },
            color: "#fff",
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        datalabels: {
          display: false,
          clip: false,
          anchor: "end",
          align: "start",
          offset: -20,
          color: "#fff",
          font: {
            size: 12,
            weight: "bold",
          },
          formatter: function (value) {
            return value != null ? value : "";
          },
        },
        tooltip: {
          callbacks: {
            title: function (items) {
              const firstItem = Array.isArray(items) ? items[0] : null;
              const metadata = firstItem?.dataset?.metadata?.[firstItem?.dataIndex] || {};
              if (metadata.tooltipType === "envDetailPopup") return "";
              return formatDayDateTitle(firstItem?.label, metadata);
            },
            label: function (context) {
              const { dataset, dataIndex } = context;
              const value = dataset.data?.[dataIndex];
              const metadata = dataset.metadata?.[dataIndex] || {};
              const label = dataset.label?.toLowerCase() || "";

              if (value === null || value === undefined) return "";

              const lines = [];

              const formatNumber = (val) => {
                const num = Number(val);
                return Number.isFinite(num) ? num.toFixed(1) : null;
              };
              const formatNumberWithDecimals = (val, decimals) => {
                const num = Number(val);
                return Number.isFinite(num) ? num.toFixed(decimals) : null;
              };
              const formatDeviation = (val) => {
                const formatted = formatNumber(val);
                return formatted ? `Deviation: ${formatted}%` : null;
              };
              const formatTargetSuffix = (target) => {
                if (Array.isArray(target)) {
                  const [minRaw, maxRaw] = target;
                  const min = formatNumber(minRaw);
                  const max = formatNumber(maxRaw);
                  if (min && max) {
                    return ` (${min}-${max})`;
                  }
                  const single = min ?? max;
                  return single ? ` (${single})` : "";
                }
                const formatted = formatNumber(target);
                return formatted ? ` (${formatted})` : "";
              };
              const formatTargetSuffixInt = (target) => {
                if (Array.isArray(target)) {
                  const [minRaw, maxRaw] = target;
                  const min = Number(minRaw);
                  const max = Number(maxRaw);
                  if (Number.isFinite(min) && Number.isFinite(max)) {
                    return ` (${Math.round(min)}-${Math.round(max)})`;
                  }
                  const single = Number.isFinite(min) ? min : Number.isFinite(max) ? max : null;
                  return single !== null ? ` (${Math.round(single)})` : "";
                }
                const single = Number(target);
                return Number.isFinite(single) ? ` (${Math.round(single)})` : "";
              };
              const formatValWithTarget = (value, target, unit) => {
                const formatted = formatNumber(value);
                if (!formatted) return "";
                return `${formatted}${unit || ""}${formatTargetSuffix(target)}`;
              };
              const formatValueOnly = (value, unit) => {
                const formatted = formatNumber(value);
                return formatted ? `${formatted}${unit || ""}` : "";
              };
              const formatDateTime = (value) => {
                if (!value) return null;
                const parsed = new Date(value);
                if (Number.isNaN(parsed.getTime())) return String(value);
                const day = String(parsed.getDate()).padStart(2, "0");
                const month = parsed.toLocaleString("en-US", { month: "short" });
                const year = parsed.getFullYear();
                const hour = String(parsed.getHours()).padStart(2, "0");
                const minute = String(parsed.getMinutes()).padStart(2, "0");
                return `${day}-${month}-${year} ${hour}:${minute}`;
              };
              const formatIsoDateTime = (value) => {
                if (!value) return null;
                const raw = String(value).trim();
                const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
                if (match) {
                  return `${match[1]}-${match[2]}-${match[3]} ${match[4]}:${match[5]}`;
                }
                const parsed = new Date(raw);
                if (Number.isNaN(parsed.getTime())) return raw;
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, "0");
                const day = String(parsed.getDate()).padStart(2, "0");
                const hour = String(parsed.getHours()).padStart(2, "0");
                const minute = String(parsed.getMinutes()).padStart(2, "0");
                return `${year}-${month}-${day} ${hour}:${minute}`;
              };
              const formatTargetRange = (target) => {
                if (target === "N/A") return "N/A";
                if (Array.isArray(target)) {
                  const [minRaw, maxRaw] = target;
                  const min = formatNumber(minRaw);
                  const max = formatNumber(maxRaw);
                  if (min && max) return `${min}-${max}`;
                  return min ?? max ?? null;
                }
                return formatNumber(target);
              };
              const pushDeviation = (val) => {
                const deviationLine = formatDeviation(val);
                if (deviationLine) lines.push(deviationLine);
              };
              if (
                ["vaccination", "sanitation", "feed", "weights", "mortality"].includes(label) &&
                metadata.message
              ) {
                const messageText = String(metadata.message);
                const words = messageText.split(/\s+/).filter(Boolean);
                const mid = Math.ceil(words.length / 2);
                const line1 = words.slice(0, mid).join(" ");
                const line2 = words.slice(mid).join(" ");
                lines.push(`${dataset.label}`);
                if (line1) lines.push(line1);
                if (line2) lines.push(line2);
              } else if (metadata.tooltipType === "lighting") {
                const avgValue = formatValueOnly(value, " lx");
                const avgDev = formatNumber(metadata.lux_avgDeviationP);
                const lowVal = formatNumber(metadata.lux_lowVal);
                const highVal = formatNumber(metadata.lux_highVal);
                const targetL = formatNumber(metadata.lux_targetL);
                const targetH = formatNumber(metadata.lux_targetH);
                if (avgValue) lines.push(`Avg Lux: ${avgValue}`);
                if (avgDev) lines.push(`Avg Dev: ${avgDev}%`);
                if (lowVal || highVal) lines.push(`Range: ${lowVal ?? "-"}-${highVal ?? "-"}`);
                if (targetL || targetH) lines.push(`Target: ${targetL ?? "-"}-${targetH ?? "-"}`);
              } else if (
                metadata.tooltipType === "lightingDarkBar" ||
                metadata.tooltipType === "lightingDarkShade"
              ) {
                const start = formatDateTime(metadata.dark_start);
                const end = formatDateTime(metadata.dark_end);
                const duration = formatNumberWithDecimals(metadata.dark_period, 2);
                const dev = formatNumber(metadata.dark_deviationp);
                const target = formatNumber(metadata.dark_target);
                if (duration !== null) lines.push(`Dark Duration: ${duration} h`);
                if (start) lines.push(`Start: ${start}`);
                if (end) lines.push(`End: ${end}`);
                if (target !== null) lines.push(`Target: ${target}`);
                if (dev !== null) lines.push(`Dev %: ${dev}`);
              } else if (metadata.tooltipType === "envDetailPopup") {
                const metricLabel = metadata.metricLabel || dataset.label || "";
                const unit = metadata.unit || "";
                const metricValue = formatValueOnly(value, unit);
                const dev = formatNumberWithDecimals(metadata.deviationP, 2);
                const target = formatTargetRange(metadata.target);
                const date = formatIsoDateTime(metadata.timestamp_name);
                if (metricValue) lines.push(`${metricLabel}: ${metricValue}`);
                if (dev !== null) lines.push(`Dev%: ${dev}`);
                if (target) lines.push(`Target: ${target}`);
                if (date) lines.push(`Date: ${date}`);
              } else if (label === "temp" || label === "avg temp") {
                const avgTempValue = formatValueOnly(value, " \u00B0C");
                if (avgTempValue) {
                  lines.push(`Avg Temp: ${avgTempValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " \u00B0C");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " \u00B0C");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "hum" || label === "avg hum") {
                const humValue = formatValueOnly(value, " %");
                if (humValue) {
                  lines.push(`Avg Hum: ${humValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " %");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " %");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "nh3" || label === "avg nh3") {
                const nh3Value = formatValueOnly(value, " ppm");
                if (nh3Value) {
                  lines.push(`Avg NH3: ${nh3Value}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " ppm");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " ppm");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "ph" || label === "avg ph") {
                const phValue = formatValueOnly(value);
                if (phValue) {
                  lines.push(`Avg pH: ${phValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal);
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal);
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "floor temp" || label === "avg ftemp" || label === "ftemp") {
                const ftempValue = formatValueOnly(value, " \u00B0C");
                if (ftempValue) {
                  lines.push(`Avg FTemp: ${ftempValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " \u00B0C");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " \u00B0C");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "lighting" || label === "lux" || label === "avg lux") {
                const luxValue = formatValueOnly(value, " lx");
                if (luxValue) {
                  lines.push(`Avg Lux: ${luxValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " lx");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " lx");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label === "orp" || label === "avg orp") {
                const orpValue = formatValueOnly(value);
                if (orpValue) {
                  lines.push(`Avg ORP: ${orpValue}${formatTargetSuffixInt(metadata.target)}`);
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal);
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal);
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (
                label === "water temp" ||
                label === "avg water temp" ||
                label === "wtemp"
              ) {
                const wtempValue = formatValueOnly(value, " \u00B0C");
                if (wtempValue) {
                  lines.push(
                    `Avg Water Temp: ${wtempValue}${formatTargetSuffixInt(metadata.target)}`
                  );
                }
                if (metadata.avgDeviationP !== undefined && metadata.avgDeviationP !== null) {
                  const formatted = formatNumber(metadata.avgDeviationP);
                  if (formatted) lines.push(`Avg Deviation: ${formatted}%`);
                } else {
                  pushDeviation(metadata.deviationp);
                }
                if (metadata.lowVal !== undefined && metadata.lowVal !== null) {
                  const lowValue = formatValueOnly(metadata.lowVal, " \u00B0C");
                  if (lowValue) lines.push(`Low: ${lowValue}`);
                }
                if (metadata.highVal !== undefined && metadata.highVal !== null) {
                  const highValue = formatValueOnly(metadata.highVal, " \u00B0C");
                  if (highValue) lines.push(`High: ${highValue}`);
                }
              } else if (label.includes("water")) {
                lines.push(`${dataset.label}: ${value} L${formatTargetSuffix(metadata.target)}`);
                const totalIncrement = Number(metadata.water_increment);
                lines.push(
                  `Total: ${Number.isFinite(totalIncrement) ? Math.round(totalIncrement) : "-"}`
                );
                pushDeviation(metadata.deviationp);
              } else if (metadata.tooltipType === "mortalityBreakdown") {
                lines.push(`Total: ${value}${formatTargetSuffix(metadata.target)}`);
                const dev = formatDeviation(metadata.deviationp);
                if (dev) lines.push(dev);
                if (typeof metadata.mortality_rate === "number") {
                  lines.push(`Mortality Rate: ${metadata.mortality_rate}%`);
                }
                if (metadata.increment !== undefined && metadata.increment !== null) {
                  lines.push(`Cum: ${metadata.increment}`);
                }
              } else if (metadata.tooltipType === "mortalityBreakdownPart") {
                lines.push(`${dataset.label}: ${value}`);
              } else if (label.includes("mortality")) {
                lines.push(`${dataset.label}: ${value}${formatTargetSuffix(metadata.target)}`);
                const dev = formatDeviation(metadata.deviationp);
                if (dev) lines.push(dev);
                if (typeof metadata.mortality_rate === "number") {
                  lines.push(`Mortality Rate: ${metadata.mortality_rate}%`);
                }
                if (metadata.increment !== undefined && metadata.increment !== null) {
                  lines.push(`Cum: ${metadata.increment}`);
                }
              } else if (label.includes("avg weight")) {
                const weightValue = formatNumberWithDecimals(value, 3);
                if (weightValue) {
                  lines.push(`Avg Weight: ${weightValue}${formatTargetSuffix(metadata.target)}`);
                }
                if (metadata.day !== undefined && metadata.day !== null) {
                  lines.push(`Day: ${metadata.day}`);
                }
                const dev = formatDeviation(metadata.deviationp);
                if (dev) lines.push(dev);
                const targetValue = formatNumber(metadata.target);
                if (targetValue) lines.push(`Target: ${targetValue}`);
              } else if (label.includes("fcr")) {
                const fcrValue = formatValueOnly(value);
                if (fcrValue) {
                  lines.push(`FCR: ${fcrValue}${formatTargetSuffix(metadata.target)}`);
                }
                if (metadata.day !== undefined && metadata.day !== null) {
                  lines.push(`Day: ${metadata.day}`);
                }
                const dev = formatDeviation(metadata.deviationp);
                if (dev) lines.push(dev);
                const targetValue = formatNumber(metadata.target);
                if (targetValue) lines.push(`Target: ${targetValue}`);
              } else {
                lines.push(`${dataset.label}: ${value}${formatTargetSuffix(metadata.target)}`);
              }
              if (
                label.includes("water") &&
                metadata.water_bird !== undefined &&
                label !== "water temp"
              ) {
                const perBirdValue = formatNumberWithDecimals(metadata.water_bird, 3);
                if (perBirdValue) {
                  lines.push(`Per Bird: ${perBirdValue} L`);
                }
              }
              if (label === "ph") {
                if (metadata.deviationp !== undefined) {
                  lines.push(`Deviation: ${metadata.deviationp.toFixed(2)}%`);
                }
              }
              return lines;
            },
            labelColor: function (context) {
              const metadata = context.dataset?.metadata?.[context.dataIndex] || {};
              const isMortalityTooltip =
                metadata.tooltipType === "mortalityBreakdown" ||
                metadata.tooltipType === "mortalityBreakdownPart" ||
                String(context.dataset?.label || "")
                  .toLowerCase()
                  .includes("mortality");

              if (!isMortalityTooltip) return undefined;

              const resolveColor = (color) => {
                if (Array.isArray(color)) return color[context.dataIndex];
                return color;
              };
              const color =
                resolveColor(context.dataset?.pointBackgroundColor) ||
                resolveColor(context.dataset?.backgroundColor) ||
                resolveColor(context.dataset?.borderColor) ||
                "#ffffff";

              return {
                borderColor: color,
                backgroundColor: color,
                borderWidth: 0,
              };
            },
          },
        },
      },
      interaction: {
        intersect: true,
        mode: "nearest",
      },
      onClick: (event, elements, chart) => {
        if (!elements?.length || typeof onPointClick !== "function") return;
        const first = elements[0];
        const dataset = chart?.data?.datasets?.[first.datasetIndex];
        const label = chart?.data?.labels?.[first.index];
        const metadata = dataset?.metadata?.[first.index] || {};
        onPointClick({
          chart,
          dataset,
          datasetIndex: first.datasetIndex,
          dataIndex: first.index,
          label,
          value: dataset?.data?.[first.index],
          metadata,
        });
      },
      scales: {
        y: {
          stacked: showStacked,
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
            color: "rgba(255, 255, 255, .2)",
          },
          ticks: {
            suggestedMin: 0,
            beginAtZero: true,
            //max: maxY,
            padding: 10,
            font: {
              size: 14,
              weight: 300,
              family: "Roboto",
              style: "normal",
              lineHeight: 2,
            },
            color: "#fff",
            /*callback: function (value) {
              return value;
            },
            maxTicksLimit: 6,*/
          },
          title: datasetsArray.some((dataset) => (dataset.yAxisID || "y") === "y")
            ? { display: false }
            : undefined,
        },
        ...(showRightAxis
          ? {
              y1: {
                position: "right",
                display: true,
                grid: {
                  drawBorder: false,
                  display: false,
                },
                ticks: {
                  beginAtZero: false,
                  padding: 10,
                  color: "#fff",
                  maxTicksLimit: 6,
                  min: y1SuggestedMin,
                  max: y1SuggestedMax,
                  stepSize: y1Step,
                  callback: (value) => (value % 1 === 0 ? Math.round(value) : ""),
                  font: {
                    size: 14,
                    weight: 300,
                    family: "Roboto",
                    style: "normal",
                    lineHeight: 2,
                  },
                },
              },
            }
          : {}),
        x: {
          stacked: showStacked,
          grid: {
            drawBorder: false,
            display: true,
            drawOnChartArea: true,
            drawTicks: false,
            borderDash: [5, 5],
            color: "rgba(255, 255, 255, .2)",
          },
          ticks: {
            display: true,
            minRotation: xAxisTickRotation,
            maxRotation: xAxisTickRotation,
            color: "#f8f9fa",
            padding: 10,
            font: {
              size: 14,
              weight: 300,
              family: "Roboto",
              style: "normal",
              lineHeight: 2,
            },
          },
        },
      },
    },
  };
}

export default configs;
